import { useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Upload, Download, Loader2, CheckCircle2, XCircle, Plus } from "lucide-react";

const download = async (url, name) => {
  const res = await api.get(url, { responseType: "blob" });
  const a = document.createElement("a"); a.href = URL.createObjectURL(res.data); a.download = name; a.click();
};

export function ClientProfileIO({ onDone }) {
  const [open, setOpen] = useState(false);
  const [pv, setPv] = useState(null);
  const [busy, setBusy] = useState(false);
  const exp = async () => { try { await download("/client-profiles-io/export?fmt=xlsx", "profil_klien.xlsx"); } catch { toast.error("Gagal mengekspor profil klien"); } };
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    setBusy(true);
    try { setPv((await api.post("/client-profiles-io/preview", fd, { headers: { "Content-Type": "multipart/form-data" } })).data); }
    catch (er) { toast.error(apiErr(er.response?.data?.detail)); }
    setBusy(false); e.target.value = "";
  };
  const commit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/client-profiles-io/commit", { rows: pv.rows });
      toast.success(`${data.updated} profil diperbarui, ${data.created} klien baru`); setOpen(false); setPv(null); onDone();
    } catch (er) { toast.error(apiErr(er.response?.data?.detail)); }
    setBusy(false);
  };
  return (
    <div className="flex gap-2">
      <Button variant="outline" size="sm" onClick={exp} data-testid="cw-export"><Download className="h-4 w-4 mr-1" />Ekspor / Template</Button>
      <Button size="sm" onClick={() => setOpen(true)} data-testid="cw-import"><Upload className="h-4 w-4 mr-1" />Impor Excel</Button>
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setPv(null); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-auto">
          <DialogHeader><DialogTitle>Impor Profil Semua Klien</DialogTitle>
            <DialogDescription className="text-xs">Unduh "Ekspor / Template" (berisi data semua klien saat ini), isi atau ubah per baris, lalu unggah. Klien dicocokkan dengan Kode Klien (atau nama). Sel kosong tidak menghapus data lama; kolom PIC / Kredensial / Checklist yang diisi menggantikan daftar lama. Kode klien baru = klien baru (khusus Super Admin).</DialogDescription></DialogHeader>
          <label className="inline-flex w-fit">
            <input type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={onFile} data-testid="cw-import-file" />
            <span className="inline-flex h-9 cursor-pointer items-center rounded-md border border-input px-3 text-sm hover:bg-secondary">
              {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}Pilih File</span>
          </label>
          {pv && (
            <div className="space-y-2" data-testid="cw-import-preview">
              <p className="text-xs">{pv.total} baris · <span className="text-emerald-700">{pv.valid} valid</span> · {pv.create} klien baru · <span className="text-rose-600">{pv.total - pv.valid} error</span></p>
              <div className="overflow-auto rounded-md border border-border max-h-80">
                <Table>
                  <TableHeader><TableRow className="bg-secondary/50"><TableHead>Baris</TableHead><TableHead>Kode</TableHead><TableHead>Klien</TableHead><TableHead>Hasil</TableHead></TableRow></TableHeader>
                  <TableBody>{pv.rows.map((r) => (
                    <TableRow key={r.row} data-testid={`cw-import-row-${r.row}`}>
                      <TableCell className="text-xs">{r.row}</TableCell><TableCell className="num text-xs">{r.data["client.code"]}</TableCell>
                      <TableCell className="text-xs">{r.client_name}</TableCell>
                      <TableCell className="text-xs">{!r.valid ? <span className="text-rose-600 inline-flex items-center gap-1"><XCircle className="h-3.5 w-3.5" />{r.errors.join("; ")}</span>
                        : r.action === "create" ? <span className="text-sky-700 inline-flex items-center gap-1"><Plus className="h-3.5 w-3.5" />Klien baru</span>
                          : <span className="text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" />Perbarui</span>}
                        {r.warnings?.length > 0 && <span className="block text-[10px] text-amber-700" data-testid={`cw-import-warn-${r.row}`}>{r.warnings.join("; ")}</span>}</TableCell>
                    </TableRow>))}</TableBody>
                </Table>
              </div>
            </div>
          )}
          <DialogFooter><Button onClick={commit} disabled={!pv?.valid || busy} data-testid="cw-import-commit">Simpan {pv?.valid || 0} Profil</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
